import { MLSListing } from '@aloft/toolkit-shared';
import { Flex, Typography, colors } from '@aloft/toolkit-sui';

import { Bar, CartesianGrid, Cell, ComposedChart, ReferenceLine } from 'recharts';

import { GraphXAxis } from '../../../components/graphs/components/GraphXAxis.tsx';
import { GraphYAxis } from '../../../components/graphs/components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../../../components/graphs/components/ResponsiveGraphWrapper.tsx';
import { SimpleGraphTooltip } from '../../../components/graphs/components/SimpleGraphTooltip.tsx';

const formatPrice = (n: number): string => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}m`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${n}`;
};

type HistogramBin = {
  lo: number; // numeric bin start — used as x-axis dataKey and for ReferenceLine
  rangeLabel: string; // tooltip label: "$325k – $375k"
  count: number;
};

const pickBinWidth = (range: number): number => {
  if (range < 100_000) return 2_500;
  if (range < 250_000) return 10_000;
  if (range < 500_000) return 25_000;
  if (range < 1_000_000) return 25_000;
  if (range < 2_000_000) return 50_000;
  if (range < 5_000_000) return 100_000;
  return 250_000;
};

const buildHistogram = (prices: number[]): { bins: HistogramBin[]; binWidth: number } => {
  if (prices.length === 0) return { bins: [], binWidth: 0 };
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) {
    return {
      bins: [{ lo: min, rangeLabel: formatPrice(min), count: prices.length }],
      binWidth: 0,
    };
  }

  const range = max - min;
  const binWidth = pickBinWidth(range);

  const binStart = 0;
  const binEnd = Math.ceil(max / binWidth) * binWidth;
  const numBins = Math.round((binEnd - binStart) / binWidth);

  const bins: HistogramBin[] = Array.from({ length: numBins }, (_, i) => {
    const lo = binStart + i * binWidth;
    const hi = lo + binWidth;
    const half = binWidth / 2;
    const rangeStart = i === 0 ? lo : lo - half;
    const rangeEnd = i === 0 ? hi : hi - half;
    return { lo, rangeLabel: `${formatPrice(rangeStart)} – ${formatPrice(rangeEnd)}`, count: 0 };
  });

  for (const price of prices) {
    const idx = Math.min(Math.floor((price - binStart) / binWidth), numBins - 1);
    const bin = bins[idx];
    if (bin) bin.count += 1;
  }

  return { bins, binWidth };
};

const calcMedian = (sorted: number[]): number => {
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? (sorted[mid] ?? 0)
    : ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
};

// Snap a value to the nearest bin's lo value
const snapToBin = (target: number, binWidth: number): number =>
  Math.max(0, Math.floor(target / binWidth)) * binWidth;

// Interpolate between two hex colors by ratio 0→1
const interpolateColor = (hexFrom: string, hexTo: string, t: number): string => {
  const parse = (hex: string) => {
    const h = hex.replace('#', '');
    return [
      parseInt(h.substring(0, 2), 16),
      parseInt(h.substring(2, 4), 16),
      parseInt(h.substring(4, 6), 16),
    ] as [number, number, number];
  };
  const [r1, g1, b1] = parse(hexFrom);
  const [r2, g2, b2] = parse(hexTo);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r},${g},${b})`;
};

type RegressionPriceHistogramProps = {
  listings: MLSListing[];
  mode: 'adjusted' | 'raw';
};

export const RegressionPriceHistogram = ({ listings, mode }: RegressionPriceHistogramProps) => {
  const prices = listings
    .map((l) => (mode === 'adjusted' ? l.AdjustedClosePrice : l.ClosePrice))
    .filter((p): p is number => p != null && p > 0);

  if (prices.length === 0) return null;

  const sorted = [...prices].sort((a, b) => a - b);
  const avg = prices.reduce((sum, p) => sum + p, 0) / prices.length;
  const med = calcMedian(sorted);

  const { bins, binWidth } = buildHistogram(prices);
  const maxCount = Math.max(...bins.map((b) => b.count), 1);

  const medianX = binWidth > 0 ? snapToBin(med, binWidth) : med;
  const avgX = binWidth > 0 ? snapToBin(avg, binWidth) : avg;

  const colorLight = mode === 'raw' ? '#c8dff5' : '#ded0f7';
  const colorDark = mode === 'raw' ? colors.graphChart : '#4819a6';

  return (
    <Flex flexDirection="column" gap="12px">
      <Flex alignItems="center" gap="24px" flexWrap="wrap">
        <Typography variant="h4">{mode === 'adjusted' ? 'Adjusted' : 'Raw'} Transaction Price Distribution</Typography>
        <Flex gap="16px">
          <Flex alignItems="center" gap="6px">
            <div style={{ width: 16, height: 2, backgroundColor: colors.graphChart2 }} />
            <Typography variant="body2" color="textSecondary">
              Median {formatPrice(Math.round(med))}
            </Typography>
          </Flex>
          <Flex alignItems="center" gap="6px">
            <div
              style={{
                width: 16,
                height: 2,
                backgroundImage: `repeating-linear-gradient(to right, ${colors.graphChart3} 0 4px, transparent 4px 7px)`,
                backgroundSize: '7px 2px',
              }}
            />
            <Typography variant="body2" color="textSecondary">
              Avg {formatPrice(Math.round(avg))}
            </Typography>
          </Flex>
        </Flex>
      </Flex>

      <ResponsiveGraphWrapper disableAnimations={false}>
        <ComposedChart
          data={bins}
          barCategoryGap={0}
          barGap={0}
          margin={{ top: 16, left: 8, bottom: 8, right: 8 }}
          style={{ background: colors.graphBackground, borderRadius: 8 }}
        >
          <CartesianGrid vertical={false} />
          <GraphXAxis
            dataKey="lo"
            scale="linear"
            type="number"
            domain={['dataMin', 'dataMax + binWidth']}
            tickFormatter={formatPrice}
            label="Sale Price"
          />
          <GraphYAxis label="Transactions" />
          <SimpleGraphTooltip<HistogramBin>
            disableAnimations={false}
            titleKey="rangeLabel"
            rows={[{ label: 'Transactions', key: 'count', color: 'graphChart' }]}
          />
          <Bar dataKey="count" name="Transactions" isAnimationActive barSize={binWidth}>
            {bins.map((bin) => (
              <Cell
                key={bin.lo}
                fill={interpolateColor(colorLight, colorDark, bin.count / maxCount)}
              />
            ))}
          </Bar>
          <ReferenceLine x={medianX} stroke={colors.graphChart2} strokeWidth={2} />
          <ReferenceLine
            x={avgX}
            stroke={colors.graphChart3}
            strokeWidth={2}
            strokeDasharray="4 3"
          />
        </ComposedChart>
      </ResponsiveGraphWrapper>
    </Flex>
  );
};
